import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import {
  ACCESS_LEVEL,
  AccessLevel,
  meetsAccessLevel,
  Section,
} from '@anessa/shared-models';
import { I18nService } from '../../i18n/i18n.service';
import { PermissionService } from '../../services/permissions/permission.service';
import { ProjectService } from '../../services/project/project.service';
import { Plant } from '../../../features/dashboard/services/plant/plant.model';
import {
  everyPlantSectionFor,
  isHiddenForMultiPlant,
} from '../../nav/multi-plant-visibility';
import {
  NotificationService,
  NotificationType,
} from '../../services/notification/notification-service';

/**
 * Route access guard. A route declares its requirement one of two ways:
 * - `data.section` (+ optional `data.sectionLevel`, default `view`, and
 *   `data.orSections`, any of which also grants it) — Tier-1 leveled section
 *   access; this is the target model.
 * - `data.permission` — a flat capability; used by routes gated on a general
 *   capability (e.g. `global_admin`, sync-status) that isn't a section.
 *
 * `global_admin` satisfies any section (resolved to `admin` in the service) and,
 * as before, any capability.
 */
export const permissionGuard: CanActivateFn = async (route) => {
  const permissionService = inject(PermissionService);
  const projectService = inject(ProjectService);
  const router = inject(Router);
  const notificationService = inject(NotificationService);
  const i18n = inject(I18nService);

  const requiredSection = route.data?.['section'] as Section | undefined;
  const requiredPermission = route.data?.['permission'];
  const hasSection =
    typeof requiredSection === 'string' && requiredSection.trim() !== '';
  const hasPermission =
    typeof requiredPermission === 'string' && requiredPermission.trim() !== '';

  if (!hasSection && !hasPermission) {
    return router.createUrlTree(['/']);
  }

  await projectService.projectReady$;

  const project =
    projectService.pendingProject() ?? projectService.currentProject();

  if (!project) {
    return router.createUrlTree(['/']);
  }

  //add check for whether going to a route not allowed for a multi project
  const toPath = (segments: string[]) =>
    '/' + segments.filter(Boolean).join('/');
  const parentSegments = route.pathFromRoot
    .slice(0, -1)
    .flatMap((r) => r.url.map((segment) => segment.path));
  const ownSegments = route.url.map((segment) => segment.path);
  const routePath = toPath([...parentSegments, ...ownSegments]);
  // The route's base path too: a matcher route consumes its params as its own
  // segments (`/synoptique/<id>`), so the exact path alone misses them.
  const routeBasePath = toPath([...parentSegments, ownSegments[0] ?? '']);

  if (
    project.isMultiPlant &&
    (isHiddenForMultiPlant(routePath) || isHiddenForMultiPlant(routeBasePath))
  ) {
    return router.createUrlTree(['/dashboard']);
  }

  const plant = project.plantMeta ?? project.plantsMeta?.[0];

  await permissionService.refreshPermissionsForPlant(plant, { force: true });

  const requiredLevel =
    (route.data?.['sectionLevel'] as AccessLevel) ?? ACCESS_LEVEL.VIEW;

  // `data.orSections`: the same level on any of these sections also grants it
  // (mirrors the backend's `@RequiredSection(..., { orSections })`).
  const sections: Section[] = hasSection
    ? [
        requiredSection,
        ...((route.data?.['orSections'] as Section[] | undefined) ?? []),
      ]
    : [];
  // A project-owned page in a multi-plant project needs its section on every
  // plant, as the backend requires; other routes keep the first-plant check.
  const everyPlantSection = project.isMultiPlant
    ? everyPlantSectionFor(routePath)
    : undefined;
  const allowed = everyPlantSection
    ? await hasSectionOnEveryPlant(
        permissionService,
        everyPlantSection,
        project.plantsMeta ?? [],
        requiredLevel,
      )
    : hasRouteAccess(permissionService, sections, requiredPermission, plant ?? null, requiredLevel);

  if (allowed) {
    return true;
  }

  notificationService.notify(
    i18n.t('permission_denied'),
    NotificationType.ERROR,
    3500,
  );

  // Redirect denied users to '/' (ProjectSelection, authGuard-only, NOT section-
  // gated) rather than '/dashboard'. '/dashboard' is itself gated on the
  // DASHBOARDS section, so redirecting a user who lacks that section here sent
  // them to a route that re-denied and redirected back — an infinite redirect
  // loop that also hammered /api/user-permissions (the guard force-refreshes on
  // every activation). '/' is ungated, so the loop can't form.
  return router.createUrlTree(['/']);
};

/** Whether the caller has `section` at `level` on each of `plants` (loaded fresh). */
async function hasSectionOnEveryPlant(
  permissionService: PermissionService,
  section: Section,
  plants: readonly { id: string }[],
  level: AccessLevel,
): Promise<boolean> {
  await permissionService.refreshPermissionsForPlants(plants);
  return meetsAccessLevel(
    permissionService.sectionLevelOnEveryPlant(
      section,
      plants.map((p) => p.id),
    ),
    level,
  );
}

/** The usual check: the route's section(s) or capability on one plant. */
function hasRouteAccess(
  permissionService: PermissionService,
  sections: Section[],
  requiredPermission: string,
  plant: Plant | null,
  level: AccessLevel,
): boolean {
  if (sections.length === 0) {
    return permissionService.hasPermissionForPlant(requiredPermission, plant);
  }
  return sections.some((section) =>
    meetsAccessLevel(permissionService.sectionLevelForPlant(section, plant), level),
  );
}
